import * as Yup from 'yup';

import { type GroupedAdditionalFieldConfigType, type IFeatureFlagsResponse } from '../types/response';
import { ITEM_MIN_COUNT_MESSAGE } from '../constants/validationMessages';
import { DataType, OperationType, Tab } from '../types/enums';
import { type TValidationConfig } from '../types/configs';
import { FieldPrefix } from '../constants/fieldPrefixes';

import getAdditionalFieldsSchemaShape from './getAdditionalFieldsSchemaShape';
import getNamesAndPartiesSchema from './getNamesAndPartiesSchema';
import getBeneficiarySchema from './getBeneficiarySchema';
import getConsignmentSchema from './getConsignmentSchema';
import getAttachmentSchema from './getAttachmentSchema';
import { getStringValidation } from './validations';
import getHeaderSchema from './getHeaderSchema';
import getItemSchema from './getItemSchema';
import getFeeSchema from './getFeeSchema';

export const getLicenseSchema = (
  additionalFieldsByTab: GroupedAdditionalFieldConfigType = {},
  validationConfig: TValidationConfig = {},
  featureFlags: IFeatureFlagsResponse = {} as IFeatureFlagsResponse
) => {
  const lpcoAdditionalFields = [
    ...(additionalFieldsByTab[Tab.HEADER]?.flat(2) ?? []),
    ...(additionalFieldsByTab[Tab.NAMES_AND_PARTIES]?.flat(2) ?? []),
    ...(additionalFieldsByTab[Tab.CONSIGNMENT]?.flat(2) ?? []),
    ...(additionalFieldsByTab[Tab.ATTACHED_DOCUMENT]?.flat(2) ?? []),
    ...(additionalFieldsByTab[Tab.BENEFICIARY]?.flat(2) ?? []),
  ];

  const additionalFields = lpcoAdditionalFields.filter((field) => field.dataType !== DataType.OPTIONS_FROM_DATABASE);
  const dbOptionAdditionalFields = lpcoAdditionalFields.filter(
    (field) => field.dataType === DataType.OPTIONS_FROM_DATABASE
  );

  return Yup.object().shape({
    ...getHeaderSchema(validationConfig),
    ...getNamesAndPartiesSchema(validationConfig),
    ...getConsignmentSchema(validationConfig),
    supportInfoMessage: getStringValidation({ field: 'supportInfoMessage', validationConfig }),
    additionalFields: Yup.object().when('selectedOperation', ([selectedOperation]: string[], schema) => {
      if (!additionalFields.length) {
        return schema.nullable();
      }

      return schema.shape(
        getAdditionalFieldsSchemaShape(additionalFields, FieldPrefix.lpco, selectedOperation, validationConfig)
      );
    }),
    dbOptionAdditionalFields: Yup.object().when('selectedOperation', ([selectedOperation]: string[], schema) => {
      if (!dbOptionAdditionalFields.length) {
        return schema.nullable();
      }

      return schema.shape(
        getAdditionalFieldsSchemaShape(dbOptionAdditionalFields, FieldPrefix.lpco, selectedOperation, validationConfig)
      );
    }),
    items: Yup.array()
      .when('selectedOperation', ([selectedOperation]: string[], schema) =>
        schema.of(
          getItemSchema({
            operation: selectedOperation,
            itemAdditionalFields: additionalFieldsByTab[Tab.ITEM],
            validationConfig,
            featureFlags,
          })
        )
      )
      .when('selectedOperation', ([selectedOperation]: string[], schema) => {
        if (
          ![OperationType.STORE, OperationType.UPDATE_STORE, OperationType.DELETE].includes(
            selectedOperation as OperationType
          )
        ) {
          return schema.min(1, ITEM_MIN_COUNT_MESSAGE);
        }

        return schema;
      }),
    attachedDocuments: Yup.array().when('selectedOperation', ([selectedOperation]: string[], schema) =>
      schema.of(getAttachmentSchema(selectedOperation, validationConfig))
    ),
    beneficiaries: Yup.array().when('selectedOperation', ([selectedOperation]: string[], schema) =>
      schema.of(getBeneficiarySchema(selectedOperation, validationConfig))
    ),
    fees: Yup.array().when('selectedOperation', ([selectedOperation]: string[], schema) =>
      schema.of(getFeeSchema(selectedOperation, validationConfig))
    ),
  });
};
